/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './mainsubjects.css';

// --- SVG Icons ---
const Icons = {
    Delete: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
    Add: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Notes: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"></path><path d="M8 8h8"></path><path d="M8 12h8"></path><path d="M8 16h5"></path></svg>
};

const SUBJECT_OPTIONS = [
    // Core
    "Mathematics", "English Home Language", "English FAL", "Afrikaans Home Language", "Afrikaans FAL",
    "isiZulu Home Language", "isiZulu FAL", "Sepedi Home Language", "Sepedi FAL",
    // Sciences
    "Physical Sciences", "Life Sciences", "Natural Sciences","NST", "Geography", "History",
    // Commerce
    "Accounting", "Business Studies", "Economics", "EMS",
    // Tech & Arts
    "IT", "CAT", "EGD", "Technology", "Coding & Robotics", "Visual Arts", "Drama", "Music", "Life Orientation"
];

export default function Mainsubjects() {
    const navigate = useNavigate();
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newSubject, setNewSubject] = useState({ name: '', grade: '', class_name: '' });
    const [openPaletteFor, setOpenPaletteFor] = useState(null);

    useEffect(() => {
        const loadDashboard = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_dashboard.php`);
                setSubjects(response.data.subjects || []);
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };
        loadDashboard();
    }, []);

    const handleAddSubject = async (e) => {
        e.preventDefault();
        if (!newSubject.name || !newSubject.grade) {
            alert('Please select a subject and grade.');
            return;
        }

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/add_subject.php`, {
                name: newSubject.name,
                grade: parseInt(newSubject.grade),
                class_name: newSubject.class_name
            });

            if (res.data.success) {
                window.location.reload();
            } else {
                alert('Error: ' + (res.data.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Failed to add subject.');
        }
    };

    const handleDeleteSubject = async (subjectId, subjectName, event) => {
        event.preventDefault(); // Stop link navigation
        if (!window.confirm(`Delete ${subjectName}? This action cannot be undone.`)) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/delete_subject.php`, { subject_id: subjectId });
            if (res.data.success) {
                setSubjects(subjects.filter(s => s.subject_id !== subjectId));
            } else {
                alert('Error deleting subject.');
            }
        } catch (error) {
            alert('Failed to delete subject.');
        }
    };

    // Group subjects by grade
    const groupedByGrade = subjects.reduce((acc, subject) => {
        const grade = subject.grade || 'Other';
        if (!acc[grade]) acc[grade] = [];
        acc[grade].push(subject);
        return acc;
    }, {});

    const sortedGrades = Object.keys(groupedByGrade).sort((a, b) => {
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return parseInt(a) - parseInt(b);
    });

    // Compute readable contrast color (black or white) for background hex
    const getContrastColor = (hex) => {
        if (!hex) return '#000';
        const h = hex.replace('#', '');
        const r = parseInt(h.substring(0,2), 16);
        const g = parseInt(h.substring(2,4), 16);
        const b = parseInt(h.substring(4,6), 16);
        // Perceived luminance
        const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return lum > 0.6 ? '#000000' : '#ffffff';
    };

    return (
        <div className="main-subjects-wrapper">

            {/* Header Section */}
            <div className="page-header">
                <div>
                    <h1>My Subjects</h1>
                    <p>Manage your classes, curriculum, and assessments.</p>
                </div>
            </div>

            {/* Add Subject Card */}
            <div className="add-subject-card">
                <h3>Add New Class</h3>
                <form className="add-subject-form" onSubmit={handleAddSubject}>
                    <div className="form-group">
                        <select
                            className="modern-select"
                            value={newSubject.name}
                            onChange={(e) => setNewSubject({ ...newSubject, name: e.target.value })}
                        >
                            <option value="">Select Subject...</option>
                            {SUBJECT_OPTIONS.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                        </select>
                    </div>

                    <div className="form-group grade-select">
                        <select
                            className="modern-select"
                            value={newSubject.grade}
                            onChange={(e) => setNewSubject({ ...newSubject, grade: e.target.value })}
                        >
                            <option value="">Grade...</option>
                            {[...Array(12)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <input
                            type="text"
                            className="modern-input"
                            placeholder="Class Name (e.g. 6A)"
                            value={newSubject.class_name}
                            onChange={(e) => setNewSubject({ ...newSubject, class_name: e.target.value })}
                        />
                    </div>

                    <button type="submit" className="btn-add">
                        <Icons.Add /> Add
                    </button>
                </form>
            </div>

            {/* Subject Lists */}
            {loading ? (
                <div className="loading-state">Loading subjects...</div>
            ) : sortedGrades.length > 0 ? (
                sortedGrades.map((grade) => (
                    <div key={grade} className="grade-section">
                        <div className="grade-header">
                            <h2>Grade {grade}</h2>
                            <span className="badge-count">
                                {groupedByGrade[grade].length} {groupedByGrade[grade].length === 1 ? 'Class' : 'Classes'}
                            </span>
                        </div>

                        <div className="subjects-grid">
                            {groupedByGrade[grade].map((subject) => {
                                const bg = subject.color || '';
                                const textColor = getContrastColor(bg || '#ffffff');
                                return (
                                <Link
                                    to={`/subjects/${subject.subject_id}`}
                                    key={subject.subject_id}
                                    className="subject-card"
                                    style={{ background: bg || undefined, color: bg ? textColor : undefined, textDecoration: 'none' }}
                                >
                                    <button
                                        className="btn-delete subject-delete-button"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteSubject(subject.subject_id, subject.name, e); }}
                                        title="Delete Subject"
                                    >
                                        <Icons.Delete />
                                    </button>

                                    <div className="card-top">
                                        <h3 className="subject-title">{subject.name}</h3>
                                    </div>

                                    <div className="card-meta">
                                        {subject.class_name && (
                                            <span className="meta-tag class-tag">{subject.class_name}</span>
                                        )}
                                        <span className="meta-tag count-tag">
                                            {subject.student_count || 0} Students
                                        </span>
                                        <div className="color-chooser-wrap">
                                            <button
                                                className="notes-button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/subjects/${subject.subject_id}/notes`); }}
                                                title="Open notes"
                                            >
                                                <Icons.Notes />
                                            </button>

                                            {/* Compact palette toggle */}
                                            <button
                                                className="color-toggle-button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenPaletteFor(openPaletteFor === subject.subject_id ? null : subject.subject_id); }}
                                                title="Choose color"
                                                style={{ background: bg || '#fff' }}
                                            >
                                                
                                            </button>

                                            {openPaletteFor === subject.subject_id && (
                                                <div className="color-popover" onClick={(e) => e.stopPropagation()}>
                                                    <div className="swatches">
                                                        {['#ffffff', '#f44336', '#ff9800', '#ffeb3b', '#8bc34a', '#00bcd4', '#2196f3', '#9c27b0', '#e7739a', '#607d8b','#8ffff0'].map((c) => (
                                                            <button
                                                                key={c}
                                                                className={`swatch ${subject.color === c ? 'selected' : ''}`}
                                                                onClick={async (e) => {
                                                                    e.preventDefault(); e.stopPropagation();
                                                                    try {
                                                                        await axios.post(`${process.env.REACT_APP_API_URL}/update_subject_color.php`, { subject_id: subject.subject_id, color: c });
                                                                        setSubjects(prev => prev.map(s => s.subject_id === subject.subject_id ? { ...s, color: c } : s));
                                                                        setOpenPaletteFor(null);
                                                                    } catch (err) {
                                                                        alert('Failed to save color');
                                                                    }
                                                                }}
                                                                style={{ background: c }}
                                                                title={`Set ${c}`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                                                        <button
                                                            className="popover-reset"
                                                            onClick={async (e) => {
                                                                e.preventDefault(); e.stopPropagation();
                                                                try {
                                                                    await axios.post(`${process.env.REACT_APP_API_URL}/update_subject_color.php`, { subject_id: subject.subject_id, color: '' });
                                                                    setSubjects(prev => prev.map(s => s.subject_id === subject.subject_id ? { ...s, color: '' } : s));
                                                                    setOpenPaletteFor(null);
                                                                } catch (err) {
                                                                    alert('Failed to reset color');
                                                                }
                                                            }}
                                                        >Reset</button>
                                                        <button className="popover-close" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenPaletteFor(null); }}>Close</button>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </Link>
                                );
                            })}
                        </div>
                    </div>
                ))
            ) : (
                <div className="empty-state">
                    <p>No subjects found. Add your first class above!</p>
                </div>
            )}
        </div>
    );
}